"use client";

import { SearchItem, SearchResult, SearchParams } from './types';

interface SearchResultsProps {
  searchParams: SearchParams;
  searchResult: SearchResult;
  onSearch: (params: SearchParams) => Promise<SearchResult>;
  loading?: boolean;
  error?: string;
  className?: string;
}

export function SearchResults({
  searchParams,
  searchResult,
  onSearch,
  loading = false,
  error,
  className = ""
}: SearchResultsProps) {
  const searchQuery = searchParams?.q || searchParams?.query;
  const totalItemsNumber = searchResult?.totalItems || 0;
  const itemsToRender = searchResult?.item || [];

  if (loading) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-500 mt-4">Загрузка результатов поиска...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-red-500 mb-4">{error}</p>
        <a href="/shop">
          <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Перейти в каталог
          </button>
        </a>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-gray-500 mb-4">Пожалуйста, введите поисковый запрос</p>
        <a href="/shop">
          <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Перейти в каталог
          </button>
        </a>
      </div>
    );
  }

  if (itemsToRender.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-gray-500 mb-4">
          По запросу <b>{searchQuery}</b> ничего не найдено
        </p>
        <a href="/shop">
          <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Перейти в каталог
          </button>
        </a>
      </div>
    );
  }

  return (
    <div className={className}>
      <h1 className="text-3xl md:text-4xl font-medium mb-2 md:mb-3"></h1>
      <span className="text-xl pb-5 mb-3">
        По запросу <b>{searchQuery}</b> найдено {totalItemsNumber} товаров
      </span>
      
      <div className="grid w-full gap-3 md:gap-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
        {itemsToRender.map((item, index) => (
          <SearchItemCard
            key={item.id ?? item.code ?? item._elasticId ?? index}
            item={item}
            showPrices={true}
            showDiscount={true}
          />
        ))}
      </div>
    </div>
  );
}

interface SearchItemCardProps {
  item: SearchItem;
  showPrices?: boolean;
  showDiscount?: boolean;
  className?: string;
}

function SearchItemCard({ 
  item, 
  showPrices = true, 
  showDiscount = true, 
  className = "" 
}: SearchItemCardProps) {
  const decodeHtmlEntities = (text: string) => {
    if (!text) return '';
    return text
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  };

  return (
    <div className={`relative mb-2 w-full rounded-md bg-white flex flex-col hover:text-blue-500 ${className}`}>
      <a href={`/shop/item/${item.code}`} className="flex h-full flex-col">
        <div 
          className="relative border rounded aspect-[3/4] overflow-hidden bg-white flex items-center justify-center"
          style={{
            minHeight: 0,
            position: 'relative'
          }}
        >
          <img
            src={item.picture || '/placeholder.png'}
            alt={item.name || 'Товар'}
            className="w-full h-full object-contain object-center"
            style={{
              backgroundColor: 'transparent',
              display: 'block'
            }}
          />
        </div>
        <div className="p-2">
          {showPrices && showDiscount && Number(item.priceOld) > 0 && Number(item.priceOld) !== Number(item.price) && (
            <div className="text-xs text-red-500 font-medium mb-1">
              -{Math.round(((Number(item.priceOld) - Number(item.price)) / Number(item.priceOld)) * 100)}%
            </div>
          )}
          {showPrices && (
            <div className="flex gap-2 items-center">
              {Number(item.price) > 0 && (
                <div className="font-bold text-default-900">
                  {Number(item.price).toLocaleString('ru-RU', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })} ₽
                </div>
              )}
              {showDiscount && Number(item.priceOld) > 0 && Number(item.priceOld) !== Number(item.price) && (
                <div className="text-sm text-gray-400 line-through">
                  {Number(item.priceOld).toLocaleString('ru-RU', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })} ₽
                </div>
              )}
            </div>
          )}
          <div
            className="text-sm line-clamp-3 leading-snug min-h-[3.5rem]"
            title={decodeHtmlEntities(item.name)}
          >
            {decodeHtmlEntities(item.name)}
          </div>
        </div>
      </a>
    </div>
  );
}
